import type { Client } from '@src/clients/domain/Client';
import { DeliveryTypeOption } from '@src/clients/domain/Client';
import type { AdminClientFormSchema } from '@src/clients/schemas/AdminClientFormSchema';
import type { ClientFormSchema } from '@src/clients/schemas/ClientFormSchema';
import { hasInsuranceInformation } from '@src/clients/schemas/ClientFormSchema';
import { ClientsClient } from '@src/clients/services/ClientsClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type * as yup from 'yup';

export const useClientUpdate = ({
  client,
  onSuccess,
}: {
  client: Client;
  onSuccess: (updatedClient: Client) => void;
}) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: yup.InferType<typeof AdminClientFormSchema>) => {
      return ClientsClient.update(client.id, {
        name: data.name,
        doingBusinessAs: data.doingBusinessAs,
        isAgency: data.isAgency,
        fax: data.fax,
        communicationLanguage: data.communicationLanguage,
        primaryContact: data.primaryContact,
        address: {
          ...data.address,
          addressDetails: data.address.addressDetails ?? undefined,
        },
        mailingAddress: data.mailingAddress
          ? {
              ...data.mailingAddress,
              addressDetails: data.mailingAddress.addressDetails ?? undefined,
            }
          : {
              ...data.address,
              addressDetails: data.address.addressDetails ?? undefined,
            },
        primaryDeliveryType: data.primaryDeliveryType.primaryDeliveryType
          ? {
              deliveryType: data.primaryDeliveryType.primaryDeliveryType,
              value:
                parseDeliveryTypeValue(
                  data.primaryDeliveryType.primaryDeliveryType,
                  data.primaryDeliveryType.value,
                ) ?? undefined,
            }
          : undefined,
        secondaryDeliveryType: data.secondaryDeliveryType.secondaryDeliveryType
          ? {
              deliveryType: data.secondaryDeliveryType.secondaryDeliveryType,
              value:
                parseDeliveryTypeValue(
                  data.secondaryDeliveryType.secondaryDeliveryType,
                  data.secondaryDeliveryType.value,
                ) ?? undefined,
            }
          : undefined,
        permitNotifications:
          data.permitNotifications.email || data.permitNotifications.phone
            ? {
                email: data.permitNotifications.email ?? undefined,
                phone: data.permitNotifications.phone ?? undefined,
              }
            : undefined,
        insuranceInformation: hasInsuranceInformation(data)
          ? {
              insuranceCompany:
                data.insuranceInformation?.insuranceCompany ?? undefined,
              insuranceAddressNumber:
                data.insuranceInformation?.insuranceAddressNumber ?? undefined,
              insuranceEffectiveDate: data.insuranceInformation
                ?.insuranceEffectiveDate
                ? new Date(
                    data.insuranceInformation.insuranceEffectiveDate,
                  ).toISOString()
                : undefined,
              insuranceExperienceDate: data.insuranceInformation
                ?.insuranceExperienceDate
                ? new Date(
                    data.insuranceInformation.insuranceExperienceDate,
                  ).toISOString()
                : undefined,
              insurancePolicyNumber:
                data.insuranceInformation?.insurancePolicyNumber ?? undefined,
              insuranceCoverageAmount:
                data.insuranceInformation?.insuranceCoverageAmount ?? undefined,
              insuranceAgentAmount:
                data.insuranceInformation?.insuranceAgentAmount ?? undefined,
              insurancePhoneNumber:
                data.insuranceInformation?.insurancePhoneNumber ?? undefined,
              insuranceIsOnFileWithState:
                data.insuranceInformation?.insuranceIsOnFileWithState ??
                undefined,
            }
          : undefined,
        jurisdictionIdNumbers: toJurisdictionIdNumbersPayload(data),
      });
    },
    onSuccess: async (updatedClient) => {
      onSuccess(updatedClient);
      await queryClient.invalidateQueries({
        queryKey: ['clients.list'],
      });
    },
  });

  const parseDeliveryTypeValue = (
    deliveryType: DeliveryTypeOption,
    value?: string,
  ) => {
    return deliveryType === DeliveryTypeOption.WEB_DOWNLOAD ? undefined : value;
  };

  return {
    updateClient: mutate,
    isLoading: isPending,
  };
};

const toJurisdictionIdNumbersPayload = (
  data: yup.InferType<typeof ClientFormSchema>,
) =>
  Object.entries(data.jurisdictionIdNumbers).map(([field, value]) => ({
    field,
    value: value ?? '',
  }));
